import { Node } from '@xyflow/react';

export enum nodeTypeNames {
  // Blueprint group node (special)
  GroupNode = 'GroupNode',
  NodeWorkflow = 'NodeWorkflow',
  NodeGroupBlueprint = 'NodeGroupBlueprint',
  NodeHTMLFields = 'NodeHTMLFields',
  // Normal nodes
  NodeAgency = 'NodeAgency',
  NodeAgent = 'NodeAgent',
  NodeAIKnot = 'NodeAIKnot',
  NodeComposite = 'NodeComposite',
  NodeDiagramsMermaid = 'NodeDiagramsMermaid',
  NodeDocument = 'NodeDocument',
  NodeDocumentDesigner = 'NodeDocumentDesigner',
  NodeDomainKnowledge = 'NodeDomainKnowledge',
  NodeFileMiner = 'NodeFileMiner',
  NodeFileResource = 'NodeFileResource',
  NodeFileResourcePDF = 'NodeFileResourcePDF',
  NodeGlobalConstants = 'NodeGlobalConstants',
  NodeImageGenerator = 'NodeImageGenerator',
  NodeIntellica = 'NodeIntellica',
  NodeJavascript = 'NodeJavascript',
  NodeKnowledgeSet = 'NodeKnowledgeSet',
  NodePlantUML = 'NodePlantUML',
  NodePresence = 'NodePresence',
  NodeQuickStart = 'NodeQuickStart',
  NodeQuickStartDynamic = 'NodeQuickStartDynamic',
  NodeSmartNotes = 'NodeSmartNotes',
  NodeStrategy = 'NodeStrategy',
  NodeWebExtract = 'NodeWebExtract',
  NodeWebSearch = 'NodeWebSearch', // Add new web search node type
  NodeFacilitator = 'NodeFacilitator', // AI Chat facilitator with project intelligence
  NodeResearcher = 'NodeResearcher', // AI Research assistant with project intelligence
}


// Define the type for the node object that will be passed to the react-flow component
export type CustomNode<T> = Omit<Node, 'data'> & {
  data: CustomNodeData<T>;
};

// Define the type for the data object that will be passed to the node components
export type CustomNodeData<T> = {
  nodetype?: string;
  locked?: boolean;
  editor?: boolean;
  label: string;
  title?: string;
  subtitle?: string;
  showHeader: boolean;
  showToolbar?: boolean;
  // content property removed - legacy system not used anymore
  offsetX?: number;
  offsetY?: number;
  fetcherKey?: string;
  // AI usage tracking
  actionsTotal?: number;
} & T;

export const nodeCategories = {
  Deliverable: [
    nodeTypeNames.NodeComposite,
    nodeTypeNames.NodeJavascript,
    nodeTypeNames.NodeImageGenerator,
  ],
  Synthesis: [
    nodeTypeNames.NodeDocument,
    nodeTypeNames.NodeDocumentDesigner,
    nodeTypeNames.NodeStrategy,
  ],
  Wisdom: [
    nodeTypeNames.NodeGlobalConstants,
    nodeTypeNames.NodeAgent,
    nodeTypeNames.NodeAgency,
    nodeTypeNames.NodeIntellica,
    nodeTypeNames.NodeFacilitator,
    nodeTypeNames.NodeResearcher,
  ],
  Knowledge: [
    nodeTypeNames.NodeSmartNotes,
    nodeTypeNames.NodeFileMiner,
    nodeTypeNames.NodeKnowledgeSet,
    nodeTypeNames.NodeDomainKnowledge,
    nodeTypeNames.NodeAIKnot,
  ],
  Information: [
    nodeTypeNames.NodeQuickStart,
    nodeTypeNames.NodeQuickStartDynamic,
    nodeTypeNames.NodePlantUML,
    nodeTypeNames.NodeDiagramsMermaid,
    nodeTypeNames.NodePresence,
  ],
  Data: [
    nodeTypeNames.NodeFileResource,
    nodeTypeNames.NodeFileResourcePDF,
    nodeTypeNames.NodeWebExtract,
    nodeTypeNames.NodeWebSearch,
  ],
  Blueprint: [
    nodeTypeNames.NodeHTMLFields,
    nodeTypeNames.NodeGroupBlueprint,
    nodeTypeNames.NodeWorkflow,
  ],
};

// Updated nodeCategoryNames mapping for information classification hierarchy
export const nodeCategoryNames: {
  [key in keyof typeof nodeCategories]?: string;
} = {
  Deliverable: 'Deliverable (Final Outputs)',
  Synthesis: 'Synthesis (Combined and Refined Knowledge)',
  Wisdom: 'Wisdom (Strategic Intelligence)',
  Knowledge: 'Knowledge (Processed Information)',
  Information: 'Information (Structured Data)',
  Data: 'Data (Raw Resources)',
  Blueprint: 'Blueprint (Workflow Automation)',
};

// Mapping of node type names to user-friendly labels
export const nodeTypeNamesForUser: Partial<Record<nodeTypeNames, string>> = {
  [nodeTypeNames.GroupNode]: 'Group',
  [nodeTypeNames.NodeAgency]: 'Agency',
  [nodeTypeNames.NodeAgent]: 'Agent',
  [nodeTypeNames.NodeAIKnot]: 'AI Knot',
  [nodeTypeNames.NodeComposite]: 'Composite Document',
  [nodeTypeNames.NodeDiagramsMermaid]: 'Mermaid Diagrams',
  [nodeTypeNames.NodeDocument]: 'Document',
  [nodeTypeNames.NodeDocumentDesigner]: 'Document Designer',
  [nodeTypeNames.NodeDomainKnowledge]: 'Domain Knowledge',
  [nodeTypeNames.NodeFileMiner]: 'File Miner',
  [nodeTypeNames.NodeFileResource]: 'File Resource',
  [nodeTypeNames.NodeFileResourcePDF]: 'PDF Resource',
  [nodeTypeNames.NodeGlobalConstants]: 'Global Constants',
  [nodeTypeNames.NodeGroupBlueprint]: 'Blueprint Reference',
  [nodeTypeNames.NodeHTMLFields]: 'HTML Fields',
  [nodeTypeNames.NodeImageGenerator]: 'Image Generator',
  [nodeTypeNames.NodeIntellica]: 'Intellica',
  [nodeTypeNames.NodeJavascript]: 'Javascript',
  [nodeTypeNames.NodeKnowledgeSet]: 'Knowledge Set',
  [nodeTypeNames.NodePlantUML]: 'Diagrams',
  [nodeTypeNames.NodePresence]: 'Presence',
  [nodeTypeNames.NodeQuickStart]: 'Quick Start',
  [nodeTypeNames.NodeQuickStartDynamic]: 'Quick Start Dynamic',
  [nodeTypeNames.NodeSmartNotes]: 'Smart Notes',
  [nodeTypeNames.NodeStrategy]: 'Strategy',
  [nodeTypeNames.NodeWebExtract]: 'Web Extract',
  [nodeTypeNames.NodeWebSearch]: 'Web Search',
  [nodeTypeNames.NodeFacilitator]: 'AI Facilitator',
  [nodeTypeNames.NodeResearcher]: 'AI Researcher',
  [nodeTypeNames.NodeWorkflow]: 'Workflow',
};

export const nodeTypeDescriptions: Record<nodeTypeNames, string> = {
  NodeDocument: 'Create and edit structured documentation with markdown support.',
  NodeFileResource: 'Upload, view and manage file resources within your project.',
  NodeFileResourcePDF: 'Upload, view and manage PDF files with built-in PDF viewer and text extraction.',
  NodeJavascript: 'Write and execute JavaScript code to transform and process data.',
  NodeImageGenerator: 'Generate images using AI based on text prompts and parameters.',
  NodeQuickStart: 'Quick start node with guides to help you get started.',
  NodeQuickStartDynamic: 'Dynamic quick start node with advanced configuration and templates.',
  NodeDocumentDesigner: 'Design complex documents with structured templates and AI assistance.',
  NodeIntellica: 'AI-powered assistant for knowledge transformation and analysis.',
  NodePlantUML: 'Generate diagrams using PlantUML markup language.',
  NodeDiagramsMermaid: 'Generate interactive diagrams using Mermaid syntax with various diagram types.',
  NodeFileMiner: 'Extract and analyze information from various file formats.',
  NodeSmartNotes: 'Analyze text and provide recommendations for next steps.',
  NodeStrategy: 'Assess and track the quality of situational awareness mapping.',
  NodePresence: 'View active users, collaboration status, and enable team communication in real-time.',
  NodeAgency: 'Manage AI agents that assist with various tasks and provide intelligent automation.',
  NodeWebExtract: 'Extract content and images from web pages with scheduling and automation.',
  NodeKnowledgeSet: 'Aggregate and organize knowledge from other nodes with sectioned markdown import.',
  NodeComposite: 'Compose, organize, and publish high-quality documents from multiple knowledge sources with section reordering and PDF export.',
  NodeGlobalConstants: 'Define global constants and variables that can be referenced by all other nodes in the project.',
  NodeAgent: 'Intelligent agent that observes nodes, triggers on events, and performs automated actions with reasoning and scoring.',
  NodeDomainKnowledge: 'Gather comprehensive domain knowledge including terminology, standards, and information transformation processes.',
  NodeAIKnot: 'Execute AI operations with predefined templates for data processing, analysis, and transformation with usage tracking.',
  NodeWebSearch: 'Search the web using AI-powered search with configurable parameters, result filtering, and content extraction options.',
  NodeFacilitator: 'AI-powered project facilitator with comprehensive access to project data, databases, and internet. Provides intelligent assistance, creates insights, facilitates collaboration, and helps achieve project goals through conversational interface.',
  NodeResearcher: 'AI-powered research assistant with comprehensive access to project data and internet resources. Specializes in research methodology, data analysis, and evidence-based insights to support project objectives through conversational interface.',
  // Blueprint node descriptions
  [nodeTypeNames.NodeHTMLFields]: 'Generate HTML form fields, display templates, and interactive widgets for user interfaces.',
  [nodeTypeNames.NodeGroupBlueprint]: 'Reference and execute blueprint templates with input/output mappings and configuration.',
  [nodeTypeNames.NodeWorkflow]: 'Execute automated workflows with step management, error handling, and retry mechanisms.',
  // Visual grouping node description
  [nodeTypeNames.GroupNode]: 'Visual grouping component for organizing related nodes with expand/collapse functionality.',
};

// Node type colors for UI elements
export const nodeTypeColors: Partial<Record<nodeTypeNames, string>> = {
  [nodeTypeNames.GroupNode]: '#64748b', // slate-500 - neutral color for grouping
  [nodeTypeNames.NodeAgency]: '#6366f1', // indigo-500
  [nodeTypeNames.NodeAgent]: '#9333ea', // purple-600 for intelligent agents
  [nodeTypeNames.NodeAIKnot]: '#f59e0b', // amber-500 for AI operations
  [nodeTypeNames.NodeComposite]: '#0ea5e9', // sky-500 for compositing
  [nodeTypeNames.NodeDiagramsMermaid]: '#06b6d4', // cyan-500
  [nodeTypeNames.NodeDocument]: '#2563eb', // blue-600
  [nodeTypeNames.NodeDocumentDesigner]: '#3b82f6', // blue-500
  [nodeTypeNames.NodeDomainKnowledge]: '#7c3aed', // violet-600 for domain knowledge
  [nodeTypeNames.NodeFileMiner]: '#84cc16', // lime-500
  [nodeTypeNames.NodeFileResource]: '#16a34a', // green-600
  [nodeTypeNames.NodeFileResourcePDF]: '#dc2626', // red-600 (distinctive for PDF)
  [nodeTypeNames.NodeGlobalConstants]: '#64748b', // slate-500 for global constants
  [nodeTypeNames.NodeGroupBlueprint]: '#3b82f6', // blue-500 for blueprint reference
  [nodeTypeNames.NodeHTMLFields]: '#f59e0b', // amber-500 for HTML generation
  [nodeTypeNames.NodeImageGenerator]: '#db2777', // pink-600
  [nodeTypeNames.NodeIntellica]: '#8b5cf6', // violet-500
  [nodeTypeNames.NodeJavascript]: '#f59e0b', // amber-500
  [nodeTypeNames.NodeKnowledgeSet]: '#8b5cf6', // violet-500 for knowledge aggregation
  [nodeTypeNames.NodePlantUML]: '#0ea5e9', // sky-500
  [nodeTypeNames.NodePresence]: '#22c55e', // green-500 for presence/user connectivity
  [nodeTypeNames.NodeQuickStart]: '#10b981', // emerald-500
  [nodeTypeNames.NodeQuickStartDynamic]: '#059669', // emerald-600
  [nodeTypeNames.NodeSmartNotes]: '#eab308', // yellow-500 (Smart Notes)
  [nodeTypeNames.NodeStrategy]: '#ef4444', // red-500 for strategy node
  [nodeTypeNames.NodeWebExtract]: '#06b6d4', // cyan-500
  [nodeTypeNames.NodeWebSearch]: '#0891b2', // cyan-600 for web search
  [nodeTypeNames.NodeFacilitator]: '#7c3aed', // violet-600 for AI facilitator
  [nodeTypeNames.NodeWorkflow]: '#059669', // emerald-600 for workflow automation
};

// Get color for the node type (with fallback)
export const getColorByNodeType = (nodeType?: string): string => {
  if (!nodeType) return '#64748b'; // slate-500 as default
  return nodeTypeColors[nodeType as nodeTypeNames] || '#64748b';
};

// Get name for the node by the type
export const getNameByNodeType = (nodeTypeName: nodeTypeNames): string => {
  return nodeTypeNamesForUser[nodeTypeName] || nodeTypeName;
};

// Get description for the node by the type
export const getDescriptionByNodeType = (nodeTypeName: nodeTypeNames): string => {
  return nodeTypeDescriptions[nodeTypeName] || nodeTypeName;
};